import type { RouteLocationNormalizedLoaded } from "vue-router";
import { runtimeConfig } from "./runtime-config";

const globalOrigin = "https://pet.autumnsoft.net";
const chinaOrigin = "https://pet.autumnsoft.cn";

interface SeoCopy {
  title: string;
  description: string;
}

interface SeoRoute {
  path: string;
  indexable: boolean;
  global: SeoCopy;
  china: SeoCopy;
}

const routes: Record<string, SeoRoute> = {
  "/login": {
    path: "/login",
    indexable: true,
    global: {
      title: "Autumn Pet - Realistic Desktop Pet Studio",
      description: "Upload a real cat or dog photo and generate a realistic Windows desktop pet with lifelike actions."
    },
    china: {
      title: "Autumn Pet - 写实桌宠生成台",
      description: "上传一张真实猫狗照片，生成会坐姿、散步、打招呼、喂食和休息的写实 Windows 桌宠。"
    }
  },
  "/privacy": {
    path: "/privacy",
    indexable: true,
    global: {
      title: "Privacy Policy - Autumn Pet",
      description: "Read how Autumn Pet handles account, pet photo, generation, payment and desktop pet data."
    },
    china: {
      title: "隐私政策 - Autumn Pet",
      description: "了解 Autumn Pet 如何处理账号、宠物照片、生成任务、支付和桌宠同步数据。"
    }
  },
  "/terms": {
    path: "/terms",
    indexable: true,
    global: {
      title: "Terms of Service - Autumn Pet",
      description: "Read the Autumn Pet service terms for realistic desktop pet generation and paid credits."
    },
    china: {
      title: "服务条款 - Autumn Pet",
      description: "阅读 Autumn Pet 写实桌宠生成、额度购买、桌宠客户端和账号使用条款。"
    }
  },
  "/generate": {
    path: "/generate",
    indexable: false,
    global: {
      title: "Generate - Autumn Pet",
      description: "Autumn Pet generation workspace."
    },
    china: {
      title: "生成 - Autumn Pet",
      description: "Autumn Pet 写实桌宠生成工作台。"
    }
  },
  "/wechat-login-bridge": {
    path: "/wechat-login-bridge",
    indexable: false,
    global: {
      title: "Signing in - Autumn Pet",
      description: "Autumn Pet sign-in bridge."
    },
    china: {
      title: "正在登录 - Autumn Pet",
      description: "Autumn Pet 微信登录中转页面。"
    }
  }
};

export function applyRouteSeo(route: RouteLocationNormalizedLoaded | { path: string }) {
  const seo = resolveSeoRoute(route.path);
  const isChina = runtimeConfig.market.isChinaVersion;
  const copy = isChina ? seo.china : seo.global;
  const origin = isChina ? chinaOrigin : globalOrigin;
  const canonicalUrl = `${origin}${seo.path}`;
  const imageUrl = `${origin}/marketing/pet-companion-hero.png`;
  const robots = seo.indexable
    ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
    : "noindex,nofollow";

  document.documentElement.lang = isChina ? "zh-CN" : "en";
  document.title = copy.title;
  setMeta("name", "description", copy.description);
  setMeta("name", "robots", robots);
  setMeta("name", "googlebot", robots);
  setMeta("property", "og:site_name", "Autumn Pet");
  setMeta("property", "og:type", "website");
  setMeta("property", "og:title", copy.title);
  setMeta("property", "og:description", copy.description);
  setMeta("property", "og:url", canonicalUrl);
  setMeta("property", "og:image", imageUrl);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", copy.title);
  setMeta("name", "twitter:description", copy.description);
  setMeta("name", "twitter:image", imageUrl);
  setLink("canonical", canonicalUrl);
  setAlternate("en-AU", `${globalOrigin}${seo.path}`);
  setAlternate("zh-CN", `${chinaOrigin}${seo.path}`);
  setAlternate("x-default", `${globalOrigin}${seo.path}`);
  setStructuredData(seo, copy, canonicalUrl, imageUrl, isChina);
}

function resolveSeoRoute(path: string): SeoRoute {
  const normalized = normalizePath(path);
  return routes[normalized] ?? routes["/login"];
}

function normalizePath(path: string) {
  const clean = (path || "/login").split("?")[0].split("#")[0].replace(/\/+$/, "");
  if (!clean || clean === "/") {
    return "/login";
  }
  if (clean.startsWith("/privacy")) {
    return "/privacy";
  }
  if (clean.startsWith("/terms")) {
    return "/terms";
  }
  if (clean.startsWith("/generate")) {
    return "/generate";
  }
  if (clean.startsWith("/wechat-login-bridge")) {
    return "/wechat-login-bridge";
  }
  return "/login";
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${cssEscape(key)}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${cssEscape(rel)}"]:not([hreflang])`);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
}

function setAlternate(hreflang: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${cssEscape(hreflang)}"]`);
  if (!element) {
    element = document.createElement("link");
    element.rel = "alternate";
    element.hreflang = hreflang;
    document.head.appendChild(element);
  }
  element.href = href;
}

function setStructuredData(seo: SeoRoute, copy: SeoCopy, canonicalUrl: string, imageUrl: string, isChina: boolean) {
  const id = "autumn-pet-structured-data";
  let element = document.getElementById(id) as HTMLScriptElement | null;
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  const json = seo.path === "/login"
    ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Autumn Pet",
        applicationCategory: "LifestyleApplication",
        operatingSystem: "Windows",
        url: canonicalUrl,
        description: copy.description,
        image: imageUrl,
        publisher: {
          "@type": "Organization",
          name: "Autumn Soft",
          url: isChina ? "https://www.autumnsoft.cn" : "https://www.autumnsoft.net"
        },
        offers: {
          "@type": "Offer",
          priceCurrency: isChina ? "CNY" : "AUD"
        }
      }
    : {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: copy.title,
        url: canonicalUrl,
        description: copy.description,
        isPartOf: {
          "@type": "WebSite",
          name: "Autumn Pet",
          url: isChina ? chinaOrigin : globalOrigin
        }
      };

  element.textContent = JSON.stringify(json);
}

function cssEscape(value: string) {
  return value.replace(/"/g, "\\\"");
}
